/**
 * Sticky header for event editor: preview card, progress, save button.
 */

import { IconCalendar, IconEye } from "../../../../components/Icons.tsx";
import { EventEditorProgress } from "./EventEditorProgress.tsx";
import { haptic } from "../../../../lib/telegram.ts";

export interface EventEditorHeaderProps {
  title: string;
  statusLabel: string;
  statusBgColor: string;
  statusColor: string;
  imageUrl: string | null;
  imageCacheBust?: number;
  startsAtDisplay: string;
  progressFilled: number;
  progressTotal: number;
  isSaving: boolean;
  hasUnsavedChanges?: boolean;
  onSave: () => void;
  onPreview: () => void;
  className?: string;
}

export function EventEditorHeader({
  title,
  statusLabel,
  statusBgColor,
  statusColor,
  imageUrl,
  imageCacheBust = 0,
  startsAtDisplay,
  progressFilled,
  progressTotal,
  isSaving,
  hasUnsavedChanges = false,
  onSave,
  onPreview,
  className = "",
}: EventEditorHeaderProps) {
  return (
    <header
      className={`sticky top-0 z-20 bg-white/95 backdrop-blur-sm border-b border-slate-100 -mx-4 px-4 pb-4 pt-2 ${className}`}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
          {imageUrl ? (
            <img
              src={
                imageCacheBust
                  ? `${imageUrl}${imageUrl.includes("?") ? "&" : "?"}_t=${imageCacheBust}`
                  : imageUrl
              }
              alt=""
              className="w-full h-full object-cover"
            />
          ) : (
            <IconCalendar className="w-6 h-6 text-slate-500" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-[15px] font-semibold text-slate-900 truncate">
            {title || "Новое мероприятие"}
          </h1>
          <div className="flex items-center gap-2 mt-0.5">
            <span
              className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${statusBgColor} ${statusColor}`}
            >
              {statusLabel}
            </span>
            {startsAtDisplay && (
              <span className="text-[12px] text-slate-500">
                · {startsAtDisplay}
              </span>
            )}
            {hasUnsavedChanges && (
              <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            haptic("light");
            onPreview();
          }}
          className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 active:bg-slate-200 transition-colors flex-shrink-0"
          title="Предпросмотр"
        >
          <IconEye className="w-4 h-4" />
        </button>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <EventEditorProgress
            filledCount={progressFilled}
            totalCount={progressTotal}
            variant="linear"
          />
        </div>
        <button
          type="button"
          onClick={() => {
            haptic("light");
            onSave();
          }}
          disabled={isSaving}
          className="flex-shrink-0 px-4 py-2 text-[14px] font-semibold text-white bg-[var(--color-accent)] rounded-xl active:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
        >
          {isSaving ? (
            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : null}
          {isSaving ? "Сохранение..." : "Сохранить"}
        </button>
      </div>
    </header>
  );
}
